import { AlertTriangleIcon, InboxIcon, RotateCcwIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { TableCell, TableRow } from '@app/components/ui/table'
import type { ChequeMaturity, ChequeRow } from './api'

/* ── Status badge ─────────────────────────────────────────────────────────── */

const MATURITY_LABEL: Record<ChequeMaturity, string> = {
  overdue: 'Vadesi geçmiş',
  'current-month': 'Bu ay',
  upcoming: 'Vadesi gelecek',
  unknown: 'Vade bilinmiyor',
}

const MATURITY_TONE = {
  overdue: 'warn',
  'current-month': 'accent',
  upcoming: 'neutral',
  unknown: 'warn',
} as const

/**
 * Endorsement outranks maturity — an endorsed cheque left our hands, its vade
 * is the endorsee's problem. And "vadesi geçmiş" is exactly that, nothing
 * stronger: Korgun never closes an own cheque on payment, so the data cannot
 * say "ödenmedi" (see worker/api/finans.ts).
 */
export function ChequeStatusBadge({ row }: { row: ChequeRow }) {
  if (row.endorsed) {
    return <Badge tone="neutral" title="Ciro edilip tedarikçiye devredildi">Ciro edildi</Badge>
  }
  if (row.maturity === 'overdue') {
    return (
      <Badge tone="warn" title="Vade tarihi geçti; Korgun'da kapanış kaydı tutulmuyor">
        {MATURITY_LABEL.overdue}
      </Badge>
    )
  }
  return <Badge tone={MATURITY_TONE[row.maturity]}>{MATURITY_LABEL[row.maturity]}</Badge>
}

/* ── State screens (deliberately duplicated per feature, like the formatters) ── */

export function TableSkeleton({ rows = 8, cols = 8 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <TableRow key={i} aria-hidden>
          {Array.from({ length: cols }, (__, j) => (
            <TableCell key={j}>
              <span
                className="block h-2.5 animate-pulse rounded-xs bg-hairline"
                style={{ width: `${[70, 55, 90, 45, 60, 50, 40, 65][j % 8]}%` }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
      <InboxIcon className="size-5 text-ink-muted/60" aria-hidden />
      <p className="font-display text-[0.9375rem] font-semibold text-ink">{title}</p>
      <p className="max-w-sm text-data text-ink-muted">{description}</p>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}

export function ErrorState({
  title = 'Veriler yüklenemedi',
  message,
  onRetry,
}: {
  title?: string
  message: string
  onRetry?: () => void
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
      <AlertTriangleIcon className="size-5 text-kordovan" aria-hidden />
      <p className="font-display text-[0.9375rem] font-semibold text-ink">{title}</p>
      <p className="max-w-sm text-data text-ink-muted">{message}</p>
      {onRetry ? (
        <Button variant="outline" className="mt-1" onClick={onRetry}>
          <RotateCcwIcon aria-hidden />
          Yeniden dene
        </Button>
      ) : null}
    </div>
  )
}
